/** Staff sign-in: email + password, then authenticator code when enrolled. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { staffApi } from "@/platform/staffApi";

export default function StaffLogin() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [totpStep, setTotpStep] = useState(false);
  const [busy, setBusy] = useState(false);

  const fail = (error: unknown) =>
    toast({
      title: "Sign in failed",
      description: error instanceof Error ? error.message : "Check your details and try again.",
      variant: "destructive",
    });

  const submit = async () => {
    setBusy(true);
    try {
      if (totpStep) {
        await staffApi.totpVerify(email, password, code);
        navigate("/staff");
        return;
      }
      const result = await staffApi.login(email, password);
      if (result.totp_required) setTotpStep(true);
      else navigate("/staff");
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="premium-page flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Staff sign in</CardTitle>
          <CardDescription>
            {totpStep ? "Enter the 6-digit code from your authenticator app." : "Ngwees Capital back office"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!totpStep ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" autoComplete="username" value={email}
                  onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="password" type="password" className="pl-9" autoComplete="current-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()} />
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="totp">Authenticator code</Label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="totp" inputMode="numeric" maxLength={6} className="pl-9 tracking-[0.4em]"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && code.length === 6 && submit()} />
              </div>
            </div>
          )}
          <Button className="w-full" onClick={submit}
            disabled={busy || (totpStep ? code.length !== 6 : !email || !password)}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : totpStep ? "Verify" : "Sign in"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
